import { useState } from 'react'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'

import type { SubscriptionPlan } from '@snootsvet/shared/portal'

import { SubscriptionPlanPicker } from '@/customer/components/billing/subscription-plan-picker'
import { initialPlan } from '@/customer/components/billing/subscription-plans'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetPetDiscounts } from '@/customer/hooks/use-get-pet-discounts'
import { useListPlans } from '@/customer/hooks/use-list-plans'
import { routes } from '@/customer/routes'

/**
 * Portal-native plan picker for an unsubscribed pet.
 *
 * Portal counterpart to the register-flow `PlanPickerPage` (step 3a).
 * Reached from `AddPetPage` after creating a pet in the portal, or
 * from `PetsPage` via the "Continue signup" link on an unsubscribed
 * pet card. `<PetScope />` owns the `:patientId` resolution and 404s
 * on unknown pets before we get here.
 *
 * On continue we hand off to `/account/pets/:id/checkout/:plan` -
 * the portal-native checkout page, which owns the final Stripe
 * handoff.
 */
export function PetPlanPage() {
  useDocumentTitle('Pick a plan')
  const { pet, member } = useOutletContext<PetScopeContext>()
  const navigate = useNavigate()
  const [picked, setPicked] = useState<SubscriptionPlan | null>(null)
  const plans = useListPlans()
  const discounts = useGetPetDiscounts(pet.id)
  const selected = picked ?? initialPlan(plans.data?.plans ?? [])

  function handleContinue() {
    if (!selected) return
    navigate(
      `${generatePath(routes.account.pets.checkout, { patientId: pet.id })}?plan=${selected}`
    )
  }

  return (
    <PageContainer
      width="wide"
      backLink={{ to: routes.account.pets.index, label: 'Back to your pets' }}
      headline={[
        { text: 'Pick a plan', color: 'green' },
        { text: `for ${pet.name}`, color: 'pink' },
      ]}
    >
      {/* Discounts are awaited too, so an eligible member never sees the full price first.
          A failed discounts read falls back to full prices; checkout won't confirm until it loads. */}
      {plans.isPending || discounts.isPending ? (
        <LoadingSpinner />
      ) : plans.isError || !selected ? (
        <Alert variant="warning">
          <AlertTitle>We couldn&apos;t load our plans</AlertTitle>
          <AlertDescription>Please try again shortly.</AlertDescription>
          <AlertAction>
            <ContactSupportAction />
          </AlertAction>
        </Alert>
      ) : (
        <div className="flex flex-col gap-8">
          <SubscriptionPlanPicker
            value={selected}
            onValueChange={setPicked}
            region={member.region}
            plans={plans.data.plans}
            discounts={discounts.data?.discounts}
          />

          <div className="flex md:justify-end">
            <Button type="button" onClick={handleContinue} className="w-full md:w-auto md:min-w-40">
              Continue
            </Button>
          </div>
        </div>
      )}
    </PageContainer>
  )
}
